import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import config from '../../config';
import './styles.scss';

const unavailableValue = '--';

const escapeHtml = value =>
  String(value).replace(
    /[&<>'"]/g,
    character =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;'
      })[character]
  );

export const formatPopupMetric = value => {
  if (value === null || value === undefined || value === '' || typeof value === 'object') {
    return unavailableValue;
  }

  if (typeof value === 'number' || (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim()))) {
    const [integer, decimal] = String(value).split('.');
    const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimal === undefined ? formattedInteger : `${formattedInteger}.${decimal}`;
  }

  return String(value);
};

export const formatPopupPercentage = value => {
  const formattedValue = formatPopupMetric(value);
  return formattedValue === unavailableValue ? unavailableValue : `${formattedValue}%`;
};

export const buildSitePopupContent = (site, metric = {}) => {
  const siteName = escapeHtml(site?.displayName || unavailableValue);
  const metricLabel = escapeHtml(metric.label || 'Impressions');
  const metricValue = escapeHtml(formatPopupMetric(site?.[metric.key || 'impressions']));
  const reach = escapeHtml(formatPopupPercentage(site?.reach));
  const frequency = escapeHtml(formatPopupMetric(site?.frequency));
  const additionalMetrics = metric.key
    ? `<div>${metricLabel}: ${metricValue}</div>`
    : `
        <div>${metricLabel}: ${metricValue}</div>
        <div>Reach: ${reach}</div>
        <div>Frequency: ${frequency}</div>
      `;
  const bodyContent = site?.metricsLoading
    ? `
        <div class="site-popup-loading" role="status" aria-label="Loading site metrics">
          <span class="spinner-border text-primary" aria-hidden="true"></span>
          <span class="sr-only">Loading site metrics...</span>
        </div>
      `
    : `
        ${additionalMetrics}
      `;

  return `
    <div id="content">
      <div id="siteNotice"></div>
      <h3 id="firstHeading" class="firstHeading">${siteName}</h3>
      <div id="bodyContent">
        ${bodyContent}
      </div>
    </div>
  `;
};

const getSiteKey = site => {
  if (site?.id !== null && site?.id !== undefined && site.id !== '') return `id:${site.id}`;
  if (site?.siteId !== null && site?.siteId !== undefined && site.siteId !== '') return `siteId:${site.siteId}`;
  return null;
};

export const hasValidCoordinates = site => {
  if (site?.latitude === '' || site?.longitude === '') return false;

  const latitude = Number(site?.latitude);
  const longitude = Number(site?.longitude);
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
};

export const findMarkerForSite = (markers, site) => {
  const siteKey = getSiteKey(site);
  if (siteKey) {
    const markerByKey = markers.find(marker => marker.__siteKey === siteKey);
    if (markerByKey) return markerByKey;
  }

  if (!hasValidCoordinates(site)) return undefined;
  const latitude = Number(site.latitude);
  const longitude = Number(site.longitude);
  return markers.find(marker => marker.__siteLat === latitude && marker.__siteLng === longitude);
};

const MapCluster = ({ sites, handleClick, onClose, selectedSite, onInfoWindowClose, metricLabel, metricKey }) => {
  const mapDefinition = { zoom: 5, center: { lat: 25.7914933, lng: -80.1389693 } };
  // Track last manual map center
  const [mapCenter, setMapCenter] = useState(mapDefinition.center);
  const lastMapCenterRef = useRef(mapDefinition.center);
  // Track last marker click to avoid double InfoWindow
  const lastMarkerClickRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const markersRef = useRef([]);
  const clustererRef = useRef(null);
  const infoWindowRef = useRef(null);
  const sitesChangedRef = useRef(false);

  useEffect(() => {
    if (!mapInstance || !window.google) return;

    // Clean up previous markers and clusterer
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }
    markersRef.current.forEach(marker => (marker.map = null));
    markersRef.current = [];

    // Prepare marker data: coerce lat/lng to numbers and skip invalid entries
    const validSites = (Array.isArray(sites) ? sites : []).filter(hasValidCoordinates);
    const markers = validSites.map(site => {
      const lat = Number(site.latitude);
      const lng = Number(site.longitude);
      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat, lng },
        title: site.displayName
      });
      // store numeric coords on marker to make later lookups reliable
      marker.__siteLat = lat;
      marker.__siteLng = lng;
      marker.__siteKey = getSiteKey(site);
      marker.addListener('click', () => {
        lastMarkerClickRef.current = marker.__siteKey || `coordinates:${lat}:${lng}`;
        if (typeof handleClick === 'function') {
          handleClick(site);
        }
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }
        const infoWindow = new window.google.maps.InfoWindow({
          content: buildSitePopupContent(
            { ...site, metricsLoading: metricKey ? false : Boolean(site.siteId) },
            { label: metricLabel, key: metricKey }
          ),
          ariaLabel: site.displayName
        });
        if (typeof onInfoWindowClose === 'function') {
          infoWindow.addListener('closeclick', () => {
            onInfoWindowClose();
          });
        }
        infoWindow.open({ anchor: marker, map: mapInstance });
        infoWindowRef.current = infoWindow;
      });
      return marker;
    });
    markersRef.current = markers;
    // Track if sites changed for fitBounds logic
    sitesChangedRef.current = true;
    // Create the clusterer
    clustererRef.current = new MarkerClusterer({
      markers,
      map: mapInstance,
      renderer: {
        render: ({ count, position, cluster }) => {
          const div = document.createElement('div');
          div.className = 'cluster-marker';
          div.textContent = count;
          if (cluster && Array.isArray(cluster.markers)) {
            const siteNames = cluster.markers
              .map(marker => marker.title)
              .filter(Boolean)
              .join(', ');
            div.setAttribute('aria-label', `Sites in cluster: ${siteNames}`);
          }
          return new window.google.maps.marker.AdvancedMarkerElement({
            position,
            content: div
          });
        }
      }
    });

    // Fit map to bounds only when sites change
    if (!selectedSite && validSites.length > 0 && sitesChangedRef.current) {
      const bounds = new window.google.maps.LatLngBounds();
      validSites.forEach(site => {
        bounds.extend(new window.google.maps.LatLng(Number(site.latitude), Number(site.longitude)));
      });
      mapInstance.fitBounds(bounds);
      const boundsCenter = bounds.getCenter();
      lastMapCenterRef.current = { lat: boundsCenter.lat(), lng: boundsCenter.lng() };
      setMapCenter(lastMapCenterRef.current);
      sitesChangedRef.current = false;
    } else if (!selectedSite && validSites.length > 0) {
      // Only set center to lastMapCenterRef if it was updated by a site selection or fitBounds
      if (lastMapCenterRef.current && lastMapCenterRef.current !== mapDefinition.center) {
        setMapCenter(lastMapCenterRef.current);
      }
      // Do not reset to mapDefinition.center unless there is no previous center
    }
    // End of useEffect
  }, [mapInstance, sites]);

  // Open info window and center map when selectedSite changes
  useEffect(() => {
    if (!selectedSite || !mapInstance || !window.google) return;
    if (!hasValidCoordinates(selectedSite)) {
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }
      return;
    }
    const selLat = Number(selectedSite.latitude);
    const selLng = Number(selectedSite.longitude);
    // If selectedSite was set by marker click, skip showing Address/Devices
    const selectedSiteKey = getSiteKey(selectedSite) || `coordinates:${selLat}:${selLng}`;
    if (lastMarkerClickRef.current === selectedSiteKey) {
      lastMarkerClickRef.current = null;
      return;
    }
    const marker = findMarkerForSite(markersRef.current, selectedSite);
    if (marker) {
      // Close previous info window if open
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      const infoWindow = new window.google.maps.InfoWindow({
        content: buildSitePopupContent(selectedSite, { label: metricLabel, key: metricKey }),
        ariaLabel: selectedSite.displayName
      });
      if (typeof onInfoWindowClose === 'function') {
        infoWindow.addListener('closeclick', () => {
          onInfoWindowClose();
        });
      }
      infoWindow.open({ anchor: marker, map: mapInstance });
      infoWindowRef.current = infoWindow;
      // Center the map on the selected site
      setMapCenter({ lat: selLat, lng: selLng });
      // Save last center ONLY if selectedSite has valid lat/lng
      lastMapCenterRef.current = { lat: selLat, lng: selLng };
    }
  }, [selectedSite, mapInstance]);

  // Expose onClose handler to parent
  useEffect(() => {
    if (typeof onClose === 'function') {
      onClose(() => {
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
          infoWindowRef.current = null;
        }
      });
    }
  }, [onClose]);

  return (
    <div className="map-wrapper">
      <GoogleMap
        zoom={mapDefinition.zoom}
        center={mapCenter}
        options={{ disableDefaultUI: true, zoomControl: true, mapId: config.mapsApi.mapId }}
        mapContainerClassName="map-container"
        onClick={() => {}}
        onLoad={map => setMapInstance(map)}
      />
    </div>
  );
};

export default MapCluster;
